import { useCallback } from 'react';
import { Platform } from 'react-native';

/** Physical label size sent to the printer (@page + print container). */
const BIRKENSTOCK_CARD_DIMENSIONS = {
  width: '51mm',
  height: '82mm',
};

const PRINT_STYLE_ID = 'birkenstock-print-style';
const PRINT_CONTAINER_CLASS = 'birkenstock-print-container';
const PRINTING_CLASS = 'birkenstock-printing';

const buildPrintStyles = () => `
  @page {
    size: ${BIRKENSTOCK_CARD_DIMENSIONS.width} ${BIRKENSTOCK_CARD_DIMENSIONS.height};
    margin: 0;
  }
  html.${PRINTING_CLASS},
  body.${PRINTING_CLASS} {
    margin: 0 !important;
    padding: 0 !important;
    width: ${BIRKENSTOCK_CARD_DIMENSIONS.width} !important;
    height: ${BIRKENSTOCK_CARD_DIMENSIONS.height} !important;
    background: #ffffff !important;
  }
  body.${PRINTING_CLASS} *:not(.${PRINT_CONTAINER_CLASS}):not(.${PRINT_CONTAINER_CLASS} *) {
    display: none !important;
  }
  .${PRINT_CONTAINER_CLASS} {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: ${BIRKENSTOCK_CARD_DIMENSIONS.width} !important;
    height: ${BIRKENSTOCK_CARD_DIMENSIONS.height} !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  .${PRINT_CONTAINER_CLASS} > div {
    width: ${BIRKENSTOCK_CARD_DIMENSIONS.width} !important;
    height: ${BIRKENSTOCK_CARD_DIMENSIONS.height} !important;
    margin: 0 !important;
  }
`;

const useBirkenstockPrint = (
  printRef: React.RefObject<HTMLDivElement>,
  onAfterPrint?: () => void
) =>
  useCallback(() => {
    if (
      Platform.OS !== 'web' ||
      typeof window === 'undefined' ||
      typeof document === 'undefined'
    ) {
      return;
    }

    const sourceNode = printRef.current;
    if (!sourceNode) return;

    const cloneNode = sourceNode.cloneNode(true) as HTMLDivElement;
    cloneNode.removeAttribute('style');
    cloneNode.style.cssText = '';

    const printContainer = document.createElement('div');
    printContainer.className = PRINT_CONTAINER_CLASS;
    printContainer.appendChild(cloneNode);
    printContainer.style.cssText = `
      position:relative;
      width:${BIRKENSTOCK_CARD_DIMENSIONS.width};
      height:${BIRKENSTOCK_CARD_DIMENSIONS.height};
      display:flex;
      align-items:center;
      justify-content:center;
      margin:0;
      padding:0;
    `;

    const styleTag = document.createElement('style');
    styleTag.id = PRINT_STYLE_ID;
    styleTag.textContent = buildPrintStyles();

    document.head.appendChild(styleTag);
    document.body.appendChild(printContainer);
    document.body.classList.add(PRINTING_CLASS);
    document.documentElement.classList.add(PRINTING_CLASS);

    let isCleaned = false;

    const cleanup = () => {
      if (isCleaned) return;
      isCleaned = true;

      document.body.classList.remove(PRINTING_CLASS);
      document.documentElement.classList.remove(PRINTING_CLASS);

      if (styleTag.parentNode) {
        styleTag.parentNode.removeChild(styleTag);
      }

      if (printContainer.parentNode) {
        printContainer.parentNode.removeChild(printContainer);
      }

      window.removeEventListener('afterprint', cleanup);
      window.removeEventListener('focus', cleanup);

      if (onAfterPrint) {
        onAfterPrint();
      }
    };

    const waitForImages = () => {
      const images = Array.from(
        printContainer.getElementsByTagName('img')
      );

      if (images.length === 0) return Promise.resolve();

      return Promise.all(
        images.map((img) => {
          if (img.complete && img.naturalWidth > 0) {
            return Promise.resolve();
          }

          return new Promise<void>((resolve) => {
            const handleDone = () => {
              img.removeEventListener('load', handleDone);
              img.removeEventListener('error', handleDone);
              resolve();
            };

            img.addEventListener('load', handleDone);
            img.addEventListener('error', handleDone);
          });
        })
      );
    };

    const fontsReady =
      (
        document as unknown as {
          fonts?: { ready: Promise<void> };
        }
      ).fonts?.ready ?? Promise.resolve();

    let hasPrinted = false;

    const startPrint = () => {
      if (hasPrinted) return;
      hasPrinted = true;

      window.addEventListener('afterprint', cleanup);
      window.addEventListener('focus', cleanup); // fallback for some browsers

      window.print();

      // safety fallback
      setTimeout(cleanup, 1500);
    };

    const fallback = window.setTimeout(startPrint, 750);

    Promise.all([waitForImages(), fontsReady]).then(() => {
      window.clearTimeout(fallback);
      requestAnimationFrame(startPrint);
    });
  }, [printRef, onAfterPrint]);

export default useBirkenstockPrint;
